// «Умные помощники» — клиент нового раздела (/api/v1/assistants), фаза 3.
// Стиль api/flows.ts: тонкая обёртка над api.request с типизированной ошибкой
// (код ошибки из payload бэка → ветвление UX: assistants_disabled, insufficient_credits,
// rate_limited, bad_request с полями). Ответы бэка уже в snake_case-контракте и
// используются как есть — конвертация не нужна, поля читаются напрямую.
import api, { API_URL } from './client'

export class AssistantsApiError extends Error {
	code: string
	status: number
	extra: Record<string, unknown>

	constructor(message: string, code: string, status: number, extra: Record<string, unknown>) {
		super(message)
		this.name = 'AssistantsApiError'
		this.code = code
		this.status = status
		this.extra = extra
	}
}

type RawErr = Error & { status?: number; payload?: unknown }

export function toAssistantsError(err: unknown): AssistantsApiError {
	if (err instanceof AssistantsApiError) return err
	const e = err as RawErr
	const payload = (e?.payload && typeof e.payload === 'object' ? e.payload : {}) as Record<string, unknown>
	const code = typeof payload.error === 'string' ? payload.error : 'request_failed'
	const message = typeof payload.message === 'string' ? payload.message : e?.message || 'Ошибка запроса'
	const { error: _c, message: _m, ok: _ok, ...extra } = payload
	void _c; void _m; void _ok
	return new AssistantsApiError(message, code, e?.status ?? 0, extra)
}

async function call<T>(endpoint: string, options?: { method?: string; body?: unknown; skipAuth?: boolean }): Promise<T> {
	try {
		return await api.request<T>(endpoint, options)
	} catch (err) {
		throw toAssistantsError(err)
	}
}

const BASE = '/api/v1/assistants'

// ── типы контракта ──────────────────────────────────────────────

export type FieldType = 'chips' | 'select' | 'text' | 'area' | 'number' | 'money' | 'date' | 'file' | 'party' | 'birth' | 'items' | 'advance_items' | 'adjust_items' | 'ledger_items'
/** Кнопка «Из истории» у строки (09.09): original — исходная счёт-фактура для корректировочной, correction — документ для исправленного экземпляра. */
export type HistoryMode = 'original' | 'correction'

export interface SolutionField {
	key: string
	type: FieldType
	label: string
	placeholder?: string
	hint?: string
	required?: boolean
	options?: string[]
	side?: 'from' | 'to'
	/** Полные реквизиты и у адресата: банк, подписант и основание (договоры, акты). */
	full?: boolean
	/** Банковские реквизиты стороны обязательны (счёт на оплату): из них шапка-платёжка и QR оплаты. */
	bank?: boolean
	max_len?: number
	history?: HistoryMode
	/** Раздел формы (09.09): подряд идущие поля одного раздела сворачиваются в блок с заголовком. */
	group?: string
	/** Данные рождения без места: числам хватает имени и даты, город тогда не спрашиваем. */
	needs_place?: boolean
	/** Данные рождения без времени: нумерологии оно не нужно, и поле не показываем. */
	needs_time?: boolean
	/** Имя обязательно: по нему считают числа имени (нумерология); карте имя не нужно. */
	needs_name?: boolean
	/** Условный показ: поле видно, если опорное поле принимает одно из values (пустые — «заполнено»). */
	visible_if?: { key: string; values: string[] }
	/** Область с кнопкой «Заполнить по описанию»: модель разбирает рассказ о сделке в поля формы. */
	describe?: boolean
}

/** «Заполнить по описанию»: входы по ключам формы и до трёх уточняющих вопросов. */
export interface DescribeResult {
	inputs: Record<string, unknown>
	questions: string[]
	/** Списано по факту (пол — цена модели, доплата по объёму). */
	charged?: number
}

/** Сторона из старого документа («Заполнить из документа», 10.09): роль в документе и реквизиты, какие нашлись. */
export interface ExtractedParty {
	role?: string
	name: string
	type?: PartyType
	inn?: string
	kpp?: string
	ogrn?: string
	addr?: string
	head?: string
	basis?: string
	email?: string
	phone?: string
	bank?: string
	bik?: string
	account?: string
	corr?: string
}
export interface ExtractResult {
	inputs: Record<string, unknown>
	parties: ExtractedParty[]
	questions: string[]
	/** «стороны, сумма, срок…» — что нашлось, подписями полей. */
	found: string[]
	charged: number
	upload: { id: string; name: string; pages: number | null }
}

/** Живой предпросмотр договора из библиотеки условий: блоки как у запуска, пропуски «[укажите: …]» списком. */
export interface ContractPreview {
	name: string
	blocks: RunBlock[]
	missing: string[]
}

/** Документ клиента из истории для кнопки «Из истории»: имя версии, номер и дата, контрагент, сумма, позиции. */
export interface SourceDoc {
	run_id: string
	form: FormKind | string
	name: string
	number: string
	date: string
	date_short: string
	counterparty: string
	total: string
	items: number
	correction_no: string
	created_at: string | null
}

// ── реквизиты (фаза 4) ──
export type PartyType = 'person' | 'ip' | 'company'

/** Сторона документа: снапшот профиля отправителя (с profile_id) или адресат. */
/** Данные рождения (астрология): место только из подсказок — нужны координаты и часовой пояс
 *  идентификатором («Europe/Moscow»), потому что карта считается по сдвигу НА ДАТУ рождения. */
export interface BirthValue {
	profile_id?: string
	name?: string
	/** ISO «1990-07-14». */
	date?: string
	/** «13:45»; пусто — время неизвестно, дома и Асцендент не строятся. */
	time?: string
	place?: string
	lat?: number | null
	lon?: number | null
	tz?: string
}

export type HoroscopePeriod = 'day' | 'week' | 'month'

/** Гороскоп периода: один на всех читателей, приходит из кэша сервера — генерации по запросу нет. */
export interface HoroscopePayload {
	period: HoroscopePeriod
	period_key: string
	label: string
	start: string
	end: string
	signs: { key: string; title: string; glyph: string; text: string }[]
	sky: { retro: string[]; events: string[]; moon: string }
	disclaimer: string
}

export interface BirthProfile {
	id: string
	name: string
	date: string
	time: string
	place: string
	lat: number | null
	lon: number | null
	tz: string
	is_default: boolean
}

/** Подсказка города: координаты и часовой пояс приходят вместе с названием. */
export interface PlaceSuggestion {
	place: string
	lat: number
	lon: number
	tz: string
	hint: string
}

export interface PartyValue {
	profile_id?: string
	type?: PartyType
	name?: string
	inn?: string
	kpp?: string
	ogrn?: string
	/** Дата присвоения ОГРНИП (ISO) — у подписи ИП в счёте-фактуре 2026. */
	ogrn_date?: string
	addr?: string
	head?: string
	/** Основание полномочий подписанта («Устава», «доверенности № … от …») — преамбула договора, формы. */
	basis?: string
	/** Главный бухгалтер (формы: счёт-фактура, УПД) — только у юрлица. */
	accountant?: string
	/** Режим НДС продавца: exempt (освобождение) | 0 | 5 | 7 | 10 | 22 — ставка по умолчанию у позиций. */
	vat_mode?: string
	/** «Работаю без печати» — М.П. в формах не печатается (у физлица всегда). */
	no_seal?: boolean
	phone?: string
	email?: string
	bank?: Record<string, string> | string
}

export interface PartyProfile {
	id: string
	type: PartyType
	name: string
	inn: string
	kpp: string
	ogrn: string
	addr: string
	head: string
	/** Основание полномочий подписанта; у старых ответов бэка может отсутствовать. */
	basis?: string
	ogrn_date?: string
	accountant?: string
	vat_mode?: string
	no_seal?: boolean
	phone: string
	email: string
	bank: Record<string, string>
	is_default: boolean
	has_signature: boolean
	/** Presigned-ссылка на сохранённую подпись (фаза 6). */
	signature_url?: string | null
	created_at: string | null
	updated_at: string | null
}

export type PartyProfileInput = Omit<PartyProfile, 'id' | 'is_default' | 'has_signature' | 'created_at' | 'updated_at'> & { is_default?: boolean | null }

export interface LookupResult {
	found: boolean
	party: PartyValue
	status: string | null
	status_label: string | null
	edo: boolean | null
}

export type OutputKind = 'doc' | 'analysis' | 'plan' | 'table' | 'contract' | 'form' | 'reading'
/** Форма, которую собирает код без модели (output.kind = form): акт, счёт, УПД, счёт-фактура. */
export type FormKind = 'act' | 'bill' | 'upd' | 'invoice' | 'advance' | 'adjustment' | 'reconciliation' | 'torg12'
/** xml — XML ФНС (УПД 5.03): акт, УПД, счета-фактуры — для ЭДО и загрузки в 1С (26.09). */
export type FileFormat = 'docx' | 'pdf' | 'xlsx' | 'xml'
/** Сторона шапки документа: справа (ГОСТ) или слева — выбор клиента у писем (output.header_choice). */
export type HeaderSide = 'right' | 'left'

export interface CatalogSolution {
	id: string
	agent_id: string
	slug: string
	title: string
	description: string
	icon: string
	sort_order: number
	is_visible: boolean
	fields: SolutionField[]
	/** template_title — заголовок капителью для шаблона документа до генерации («ПРЕТЕНЗИЯ»); пусто — серая полоса. */
	output: { kind: OutputKind; formats: string[]; template_title?: string; header_choice?: boolean; form?: FormKind; template?: string; compute?: string; params?: Record<string, unknown> }
	clarify: { max_questions: number }
	/** included_pages/extra_page_credits/scan_page_credits — доплата за объём вложений (pricing.ts); 0 — статичная цена. */
	files: { accept: string[]; max_mb: number; vision_pages: number }
	sign: { required: 'none' | 'pep' | 'ukep' }
	dispatch: { channels: string[]; deadline_days: number; next_solution_id: string | null }
	share: { allowed: boolean; allow_download: boolean }
	/** Цена по правилу сайта (09.09): у модельных решений — credits_price модели и «от» (price_from), у форм — цена из админки.
	 *  Поля может не быть у старого API — тогда «от» решает workspaceState.priceFrom по виду решения. */
	price_credits: number
	price_from?: boolean
}

export interface CatalogAgent {
	id: string
	slug: string
	title: string
	subtitle: string
	tagline: string
	icon_url: string
	category_id: string | null
	greeting: string
	can_chat: boolean
	price_chat: number
	/** Свой вход в чат (11.09): пусто — «Просто спросить». */
	chat_title?: string
	chat_hint?: string
	/** Мета посадочной /assistants/<слуг>: из админки, пусто — собираем из названия и слогана. */
	seo_title?: string
	seo_description?: string
	provider: string
	model: string
	smart_visible: boolean
	smart_sort_order: number
	solutions: CatalogSolution[]
	/** Решения на карточке главной раздела по порядку — выбор админа (23.10); пусто или нет поля — правило
	 *  по умолчанию (showcase.cardSolutions: жест дня и первые решения каталога). */
	home_solution_ids?: string[]
	/** Публичный рейтинг по проверенным отзывам (24.09): null — тумблер выключен или оценок меньше пяти. */
	rating?: { avg: number; n: number } | null
}

export interface CatalogCategory {
	id: string
	slug: string
	title: string
	sort_order: number
	is_visible: boolean
}

export interface Catalog {
	staff: boolean
	enabled: boolean
	categories: CatalogCategory[]
	agents: CatalogAgent[]
	/** Закреплённые помощники по порядку: стоят первыми в рельсе (раздел с 23.10 открывается
	 *  на главной, а не на закреплённом). Скрепка живёт на аккаунте, поэтому едет за
	 *  человеком между устройствами. Пусто — гость или никто не закреплён. */
	pinned_agent_ids?: string[]
	/** Сколько скрепок разрешено. Приходит с бэка — число не дублируем на фронте. */
	max_pins?: number
}

export type RunStatus =
	| 'queued_clarify' | 'clarifying' | 'queued_generate' | 'generating' | 'queued_edit' | 'done' | 'failed' | 'cancelled'

export const ACTIVE_STATUSES: RunStatus[] = ['queued_clarify', 'queued_generate', 'generating', 'queued_edit']
export const isRunActive = (status: RunStatus | string | null | undefined): boolean =>
	!!status && (ACTIVE_STATUSES as string[]).includes(status)

export interface RunBlock {
	id: string
	t: 'h' | 'kv' | 'p' | 'list' | 'note' | 'risks' | 'steps' | 'table' | 'sign' | 'spread' | 'entity' | 'branches' | 'matrix' | string
	v?: unknown
	/** Чтение (ядро 15.09): id сущности расчёта у блока разбора. */
	entity?: string
	title?: string | null
	/** Заголовок: предмет документа строкой под видом («о возврате денежных средств…»). */
	sub?: string | null
	/** Список нумерованный («Требую», «Прошу», «Приложения»). */
	ordered?: boolean
	/** Договор: номер пункта («1.1»), ссылка шапки приложения, краткие подписи. */
	n?: string | number
	ref?: string
	compact?: boolean
	/** Только сайт (template.ts): заглушки шаблона до генерации — ширины серых полос у списка, флаг у h/kv/sign/risks; t:'ph' — абзац полос. Бэк не шлёт. */
	ph?: number[] | boolean
	header?: string[]
	rows?: string[][]
	/** Таблица формы: выравнивание колонок (l|c|r), строки итогов, ширины (доли). */
	align?: string[]
	foot?: string[][]
	widths?: number[]
}

/** Подпись версии (фаза 6): ПЭП рисунком, напечатанным именем или сохранённой; ключ проверки в штампе. */
export interface SignedInfo {
	method: 'pep' | 'ukep' | string
	method_label: string
	mode: 'draw' | 'text' | 'saved' | string
	who: string
	for_whom: string | null
	signed_at: string | null
	signed_at_label: string
	verify_key: string
	doc_hash: string
	version_n: number
	sig_image_key?: string | null
	text?: string | null
}

export interface RunVersion {
	id: string
	n: number
	name: string
	change_note: string
	changed_block_ids: string[]
	author_user_id: string | null
	signed: SignedInfo | null
	/** Presigned-ссылка на росчерк подписанной версии (час). */
	sig_image_url?: string | null
	has_docx: boolean
	has_pdf: boolean
	/** Excel у форм (акт, счёт, УПД). */
	has_xlsx?: boolean
	has_xml?: boolean
	created_at: string | null
	/** Блоки есть у последней версии (и у всех при all_blocks=1). */
	blocks?: RunBlock[]
	/** Фаза 5: имя автора правки и признак «правил соавтор». */
	author_name?: string
	is_coauthor?: boolean
}

export interface RunMessage {
	id: string
	role: 'user' | 'assistant' | 'system' | 'guest' | string
	kind: 'consult' | 'clarify' | 'answer' | 'edit' | 'comment' | 'event' | string
	text: string
	status: 'pending' | 'done' | 'failed' | string
	version_id: string | null
	guest_name: string | null
	created_at: string | null
	/** Фаза 5: автор сообщения соавтора/гостя, кто платил, id автора. */
	author_name?: string | null
	payer?: PayRule | null
	user_id?: string | null
	/** Маршрутизация (09.09): инструменты, на которые сослался помощник, — карточки перехода под пузырём. */
	links?: ToolLink[]
	/** Продолжения (Ф2, 23.09): следующие вопросы под ответом — чипы в чате и в карточке. */
	branches?: BranchAsk[]
	/** Цена сообщения по правилу сайта: пол (цена модели) при отправке и списано по факту после ответа. */
	charged?: number | null
	floor?: number | null
	/** Ветка чтения (ядро 15.09): по какой карте/вопросу; free — без списания. */
	branch?: BranchAsk | null
	free?: boolean | null
}

export interface RunPricing {
	policy: 'model' | 'fixed'
	model_price: number
	multiplier: number
	floor: number
	estimate: number
	discount_pct: number
	cost_usd?: number
	final?: number | null
	charged: number
	settle_error?: string
	/** Резерв оценки при старте (23.10): сколько списали до ответа (нет — списали пол) и сколько вернулось, если факт меньше. */
	reserved?: number
	refunded?: number
	pages?: { text_pages: number; scan_pages: number }
}

/** Оценка до запуска (POST /runs/estimate): пол «от N» и сумма по объёму вложений; точная сумма — после создания. */
export interface RunEstimate {
	policy: 'model' | 'fixed'
	floor: number
	estimate: number
	model_price: number
	discount_pct: number
	pages: { text_pages: number; scan_pages: number }
}

/** Карточка инструмента под ответом помощника: решение другого (или своего) помощника из meta.links сообщения. */
export interface ToolLink {
	tool: string
	solution_id: string
	agent_id: string
	title: string
	agent_title: string
	agent_icon: string
	price_credits: number
	price_from: boolean
	formats: string[]
}

// ── совместный доступ (фаза 5) ──
export type ShareMode = 'off' | 'view' | 'comment' | 'edit'
export type ShareRole = 'view' | 'comment' | 'edit'
export type PayRule = 'owner' | 'guest'

export interface ShareMember {
	id: string
	email: string
	name: string
	role: ShareRole
	pay: PayRule | null
	status: 'invited' | 'active' | 'revoked' | string
	status_label: string
	has_account: boolean
	invited_at: string | null
}

/** Согласование с контрагентом (09.09): пока последнюю версию не согласовали обе стороны, файлы закрыты для всех. */
export interface ApprovalState {
	required: boolean
	/** Срок согласования (ISO-дата) — напоминание, не запрет. */
	deadline: string | null
	expired: boolean
	version_n: number | null
	owner: { name: string; at: string | null } | null
	guest: { name: string; at: string | null } | null
	approved: boolean
	/** Сторона зрителя: owner — владелец (и его соавторы-staff), guest — получатель. */
	you: 'owner' | 'guest' | null
	you_approved: boolean
	can_approve: boolean
	locked: boolean
}

export interface ShareState {
	token: string
	url: string
	mode: ShareMode
	billing_default: PayRule
	allow_download: boolean
	download_locked: boolean
	approval_required: boolean
	approval_deadline: string | null
	approval?: ApprovalState
	members: ShareMember[]
	people_count: number
	price_chat: number
	is_shared: boolean
	invited?: { email: string; has_account: boolean; invite_sent: boolean }
}

export interface RunAccess {
	role: 'owner' | ShareRole | null
	via: 'owner' | 'member' | 'link' | 'staff' | null
	pay: PayRule
	can_download: boolean
	can_comment: boolean
	can_edit: boolean
	can_manage: boolean
	can_params: boolean
	can_fields: boolean
}

export interface SharedDoc {
	run_id: string
	token: string
	status: RunStatus
	stage_label: string | null
	title: string
	name: string
	owner_name: string
	created_at: string | null
	updated_at: string | null
	agent: { id: string | null; title: string | null; subtitle: string | null; icon_url: string | null }
	solution: { id: string | null; title: string | null; slug: string | null; header_side?: HeaderSide }
	versions: RunVersion[]
	messages: RunMessage[]
	approval?: ApprovalState | null
	access: RunAccess & { logged_in: boolean; price_chat: number; share_mode: ShareMode; member_status: string | null }
	promo: { agent_title: string | null; solution_title: string | null; url: string }
}

export interface RunSummary {
	id: string
	mode: 'solution' | 'chat'
	status: RunStatus
	stage: string | null
	stage_label: string | null
	title: string
	agent_id: string | null
	solution_id: string | null
	agent_title: string | null
	/** Аватарка помощника из снапшота запуска (10.09): лицо разговора в истории и в боковом меню. */
	agent_icon_url?: string | null
	/** Разговор вынесен владельцем в боковое меню сайта (10.09). */
	in_sidebar?: boolean
	/** Имя документа последней версии (10.09): подсказка в ленте документов помощника. */
	doc_name?: string | null
	solution_title: string | null
	price_credits: number
	is_test: boolean
	error_code: string | null
	error_message: string | null
	attempts: number
	created_at: string | null
	updated_at: string | null
	finished_at: string | null
	versions_count?: number
	/** Фаза 5: у своих работ — режим ссылки и число людей; у чужих — кто поделился и роль. */
	share_mode?: ShareMode | null
	people_count?: number | null
	is_shared?: boolean
	shared_by?: { owner_name: string; role: ShareRole; role_label: string } | null
}

export interface RunState extends RunSummary {
	inputs: Record<string, unknown>
	upload_ids: { id: string; field: string }[]
	/** Вложения с именами и страницами (09.09) — пузырь запроса в диалоге; есть только у тех, кому видны параметры. */
	uploads?: { id: string; field: string; name: string; pages: number | null; kind: string; parsed_by?: string; status?: string }[]
	clarifications: { question: string; answer: string | null; asked_at?: string; answered_at?: string }[]
	pending_question: string | null
	can_retry: boolean
	can_cancel: boolean
	price_chat: number
	solution: {
		title: string | null
		fields: SolutionField[] | null
		output: { kind: OutputKind; formats: string[]; header_side?: HeaderSide; form?: FormKind; compute?: string } | null
		sign: { required: string } | null
		dispatch: { channels: string[]; deadline_days: number; next_solution_id: string | null } | null
		share: { allowed: boolean; default_billing?: string; allow_download: boolean } | null
		price_credits: number | null
	}
	versions: RunVersion[]
	messages: RunMessage[]
	/** Фаза 5: владелец и права зрителя; share — только владельцу. */
	owner_name?: string
	access?: RunAccess
	share?: ShareState | null
	/** Согласование (всем, кто видит документ); null — ссылка ещё не создавалась. */
	approval?: ApprovalState | null
	/** Из чего сложилась цена запуска: база, доплата за объём вложений, скидка следующего шага. */
	/** Снимок цены запуска (09.09, правило сайта): пол списан на старте, факт (final/charged) — после ответа по себестоимости. */
	pricing?: RunPricing | null
	/** Фаза 6 (владельцу): включена ли ПЭП и оговорка о её силе. */
	sign_enabled?: boolean
	sign_note?: string
	/** Фаза 7 (владельцу): отправки, напоминание, срок ответа, следующий шаг. */
	dispatch?: DispatchState | null
	/** Ядро знаниевых помощников (15.09): тема, расчёт (доска), отметка клиента, остаток бесплатных веток. */
	node?: RunTopic | null
	computed?: RunComputed | null
	feedback?: RunFeedback
	branches_free_left?: number | null
	/** Квота бесплатных веток целиком — для счётчика «3 из 3» в карточке продолжения (23.09). */
	branches_free_total?: number | null
	/** Остаток по чтению и по суткам аккаунта: показываем меньшее; когда бьёт суточный потолок — говорим «сегодня». */
	branches_free_run_left?: number | null
	branches_free_daily_left?: number | null
}

// ── ядро знаниевых помощников (15.09): темы, расчёт, ветки, отметки ──
export interface RunTopic { id: string; slug: string; title: string }
export interface ComputedEntity { id: string; label: string; role?: string; variant?: string; position?: string }
export interface RunComputed { domain: string; kind: string; data: Record<string, unknown>; entities: ComputedEntity[]; seed: string | null }
export interface RunFeedback { useful?: boolean; came_true?: boolean; note?: string; at?: string }
/** Ветка чтения: разбор сущности (entity) или уточняющий вопрос (ask) по тем же картам. */
export interface BranchAsk { key?: string; label?: string; entity?: string; ask?: string }
export interface TopicPosition { key: string; label: string; role: string }
export interface TopicBranch { key: string; label: string; ask?: string }
export interface TopicConfig { solution_slug?: string; positions?: TopicPosition[]; branches?: TopicBranch[]; price_credits?: number; examples?: string[] }
export interface TopicNode { id: string; parent_id: string | null; slug: string; title: string; description: string; sort_order: number; config: TopicConfig }
export interface TopicTreeNode extends TopicNode { children: TopicTreeNode[] }

// ── отправка и сроки (фаза 7) ──
export type DispatchChannel = 'email' | 'post' | 'courier' | 'edo'
export type DispatchStatus = 'queued' | 'sent' | 'delivered' | 'opened' | 'accepted' | 'in_transit' | 'handed' | 'failed' | 'returned' | 'cancelled' | string

export interface Dispatch {
	id: string
	run_id: string
	version_id: string | null
	channel: DispatchChannel | string
	channel_label: string
	provider: string
	status: DispatchStatus
	status_label: string
	external_id: string | null
	tracking: string | null
	cost_credits: number
	recipient: Record<string, string>
	payload: { attachments?: string[]; cover?: string; remind?: boolean; version_n?: number; instructions?: string[]; opis?: string } & Record<string, unknown>
	events: { at: string; status: string; note: string }[]
	error: string | null
	deadline_at: string | null
	sent_at: string | null
	delivered_at: string | null
	handed_at: string | null
	created_at: string | null
	can_cancel: boolean
	manual: boolean
	active: boolean
	line: string
}

export interface Reminder {
	id: string
	status: 'scheduled' | 'sent' | 'dismissed' | string
	enabled: boolean
	due_at: string | null
	sent_at: string | null
	next_solution_id: string | null
}

export interface NextStep {
	solution_id: string
	agent_id: string
	title: string
	base_price: number
	discount_pct: number
	price: number
	available: boolean
	deadline_passed: boolean
}

export interface DispatchState {
	items: Dispatch[]
	reminder: Reminder | null
	deadline_at: string | null
	deadline_days: number
	active: boolean
	sent: boolean
	channels: string[]
	can_send: boolean
	next: NextStep | null
	stages: string[]
}

export interface QuoteChannel {
	key: DispatchChannel
	label: string
	desc: string
	ok: boolean
	why: string
	price: number
	provider: string | null
	manual: boolean
	already_sent: boolean
}

export interface DispatchQuote {
	channels: QuoteChannel[]
	recipient: Record<string, string> | null
	signed_method: string | null
	version_n: number
	doc_name: string
	signed: boolean
	signed_label: string | null
	sign_required: string
	deadline_days: number
	next_title: string | null
	cover: string
	attachments: { pdf: boolean; docx: boolean; files: number }
	agent_title: string | null
	balance: number
	email_per_day: number
}

export interface SendBody {
	channels: string[]
	email?: string
	cover?: string
	attachments?: { pdf: boolean; docx: boolean; files: boolean }
	remind?: boolean
}

export interface SendResult {
	dispatches: Dispatch[]
	deadline_at: string | null
	reminder: Reminder | null
	spent: number
}

export interface NextPrefill {
	solution_id: string
	agent_id: string
	title: string
	inputs: Record<string, unknown>
	uploads: { field: string; upload: UploadInfo }[]
	parent_run_id: string
	discount_pct: number
	base_price: number
	price: number
}

export interface VerifyInfo {
	found: boolean
	key: string
	method: string
	method_label: string
	who: string
	for_whom: string | null
	signed_at: string | null
	signed_at_label: string
	doc_name: string
	version_n: number
	agent_title: string | null
	doc_hash: string
	superseded: boolean
	note: string
}

export interface UploadInfo {
	id: string
	name: string
	mime: string
	size: number
	kind: string
	pages: number | null
	parsed_by: 'text' | 'audio' | 'image' | 'none' | string
	status: 'ready' | 'failed' | string
	error: string | null
	needs_vision: boolean
	text_preview: string
	chars: number
}

// ── ручки ───────────────────────────────────────────────────────

export const getCatalog = () => call<{ ok: boolean } & Catalog>(`${BASE}/catalog`)

/** Закрепить помощника: свежая скрепка встаёт первой (как в телеграме). */
export const pinAgent = (agentId: string) =>
	call<{ ok: boolean; pinned_agent_ids: string[]; max_pins: number }>(`${BASE}/agents/${agentId}/pin`, { method: 'POST' })

/** Снять скрепку. Повторный вызов не ошибка — кнопка не должна падать. */
export const unpinAgent = (agentId: string) =>
	call<{ ok: boolean; pinned_agent_ids: string[]; max_pins: number }>(`${BASE}/agents/${agentId}/pin`, { method: 'DELETE' })

/** Multipart-загрузка: api.request шлёт только JSON, поэтому raw fetch с тем же API_URL и токеном. */
export async function uploadFile(file: File): Promise<UploadInfo> {
	const fd = new FormData()
	fd.append('file', file)
	const token = localStorage.getItem('token')
	let res: Response
	try {
		res = await fetch(`${API_URL}${BASE}/uploads`, {
			method: 'POST',
			headers: token ? { Authorization: `Bearer ${token}` } : {},
			body: fd,
		})
	} catch {
		throw new AssistantsApiError('Нет соединения — файл не загрузился', 'network', 0, {})
	}
	let data: Record<string, unknown> = {}
	try { data = (await res.json()) as Record<string, unknown> } catch { /* пустое тело */ }
	if (!res.ok || data.ok === false) {
		const code = typeof data.error === 'string' ? data.error : 'upload_failed'
		const message = typeof data.message === 'string' ? data.message : typeof data.detail === 'string' ? data.detail : 'Файл не загрузился'
		throw new AssistantsApiError(message, code, res.status, {})
	}
	return data.upload as UploadInfo
}

export interface RunUploadRef { id: string; field: string }

export const estimateRun = (body: { solution_id: string; uploads: RunUploadRef[]; inputs: Record<string, unknown>; parent_run_id?: string | null }) =>
	call<{ ok: boolean } & RunEstimate>(`${BASE}/runs/estimate`, { method: 'POST', body })

export const createRun = (body: { solution_id: string; inputs: Record<string, unknown>; uploads: RunUploadRef[]; parent_run_id?: string | null; options?: RunOptions; node_id?: string | null }) =>
	call<{ ok: boolean; run: RunState }>(`${BASE}/runs`, { method: 'POST', body })

// ── ядро знаниевых помощников (15.09) ──
export const getTopics = (agentId: string) =>
	call<{ ok: boolean; nodes: TopicNode[]; tree: TopicTreeNode[] }>(`${BASE}/topics?agent_id=${encodeURIComponent(agentId)}`)
export const routeQuestion = (agentId: string, question: string) =>
	call<{ ok: boolean; node: { id: string; slug: string; title: string; path: string } | null }>(`${BASE}/route`, { method: 'POST', body: { agent_id: agentId, question } })
export const postBranch = (id: string, body: BranchAsk) =>
	call<{ ok: boolean; message_id: string; run: RunState }>(`${BASE}/runs/${id}/branch`, { method: 'POST', body })
export const postFeedback = (id: string, body: RunFeedback) =>
	call<{ ok: boolean; feedback: RunFeedback }>(`${BASE}/runs/${id}/feedback`, { method: 'POST', body })
/** Дневник чтений (Ф3, 15.09): свои расклады и расчёты одной лентой — карты доски, вопрос, вывод, отметка «сбылось». */
/** thumb — миниатюра карты для мелкой плитки ленты (17.09); нет её — берём image. */
export interface JournalCard { pos: string; entity: string; title: string; variant: string; image: string | null; thumb?: string | null; glyph?: string }
export interface JournalEntry {
	id: string
	created_at: string | null
	status: RunStatus
	agent_id: string | null
	agent_title: string | null
	agent_icon_url: string | null
	agent_slug: string | null
	title: string
	solution_title: string | null
	topic: string | null
	question: string
	domain: string | null
	kind: string | null
	board_title: string
	cards: JournalCard[]
	extras: { title: string; variant: string }[]
	brief: string
	feedback: RunFeedback
	outcome: boolean | null
	branches: number
	price_credits: number
}
export interface JournalAgent { id: string; slug: string; title: string; icon_url: string | null }
export const getJournal = (limit = 100) =>
	call<{ ok: boolean; entries: JournalEntry[]; agents: JournalAgent[] }>(`${BASE}/journal?limit=${limit}`)
/** Параметры запуска: сторона шапки документа; таро (Ф2) — колода и ритуал тасовки (токен сервера + касания). */
/** cards — свой расклад (Ф3): карты по позициям, «cups.03» / «major.16:rev»; главнее тасовки. */
/** seed — карта/руна дня из баннера (23.09): решение толкует ту же карту; сервер принимает только seed вида card-of-day:… / rune-of-day:…. */
export interface RunOptions { header_side?: HeaderSide; deck?: string; shuffle?: string; picks?: number[]; cards?: string[]; seed?: string }
/** faces — миниатюры четырёх карт для мини-сцен выбора способа (17.09); пусто, пока у колоды нет миниатюр. */
export interface TarotDeckPublic { slug: string; title: string; description: string; is_default: boolean; cards: number; complete: boolean; cover: string | null; faces?: string[] }
export interface TarotSpreadMeta { id: string; title: string; layout: string; count: number; positions?: { key: string; label: string }[] }
/** Умеет ли модель помощника читать фото расклада и почём (Ф3). */
export interface TarotRecognizeInfo { enabled: boolean; price: number }
export const getTarotMeta = (agentId?: string) =>
	call<{ ok: boolean; decks: TarotDeckPublic[]; spreads: TarotSpreadMeta[]; deck_count: number; recognize?: TarotRecognizeInfo }>(`${BASE}/tarot/meta${agentId ? `?agent_id=${encodeURIComponent(agentId)}` : ''}`)
/** Свой расклад по фото (Ф3): модель называет карты на снимке, человек подтверждает. Платно по цене модели. */
export interface TarotRecognized { id: string; reversed: boolean; confidence: number; name: string; sure: boolean }
export const recognizeTarot = (uploadId: string, agentId: string, count: number) =>
	call<{ ok: boolean; cards: TarotRecognized[]; note: string; charged: number }>(`${BASE}/tarot/recognize`, { method: 'POST', body: { upload_id: uploadId, agent_id: agentId, count } })
export const shuffleTarot = () => call<{ ok: boolean; token: string; expires_at: number; count: number }>(`${BASE}/tarot/shuffle`, { method: 'POST', body: {} })
/** thumb — миниатюра для галереи выбора карт (17.09); её нет у старого API и у колод без добора — тогда берём image. */
export interface TarotCardBrief { id: string; name: string; name_en: string | null; arcana: string; suit: string | null; keywords: string; image: string | null; thumb?: string | null }
export interface TarotCardFull extends TarotCardBrief { keywords_rev: string; core: string; astro: string; element: string | null; timing: string; upright: Record<string, string>; reversed: Record<string, string> }
export const getTarotCards = () => call<{ ok: boolean; cards: TarotCardBrief[] }>(`${BASE}/tarot/cards`)
export const getTarotCard = (id: string) => call<{ ok: boolean; card: TarotCardFull }>(`${BASE}/tarot/cards/${encodeURIComponent(id)}`)
export const getShareImage = (runId: string) => call<{ ok: boolean; data_url: string }>(`${BASE}/runs/${runId}/share-image`)
/** Карта дня таролога (Ф1): одна на дату для всех, без модели. */
export interface TarotDayCardData { date: string; entity: string; card: string; reversed: boolean; name: string; name_en: string | null; keywords: string; text: string; image: string | null; seed: string; personal: boolean }
/** k — ключ браузера гостя (аккаунту не нужен: сервер берёт его id). */
export const getTarotCardOfDay = (k?: string | null) => call<{ ok: boolean; card: TarotDayCardData }>(`${BASE}/tarot/card-of-day${k ? `?k=${encodeURIComponent(k)}` : ''}`)
/** Руна дня (23.09): одна на дату и человека, без модели. */
export interface RuneDayData { date: string; entity: string; rune: string; reversed: boolean; name: string; glyph: string; keywords: string; text: string; seed: string; personal: boolean }
/** Энергия дня нумеролога (23.09): аркан по дате, один на всех, без модели. */
export interface EnergyDayData { date: string; n: number; entity: string; name: string; keywords: string; text: string }
export const getEnergyOfDay = () => call<{ ok: boolean; energy: EnergyDayData }>(`${BASE}/matrix/energy-of-day`)
export const getRuneOfDay = (k?: string | null) => call<{ ok: boolean; rune: RuneDayData }>(`${BASE}/runes/rune-of-day${k ? `?k=${encodeURIComponent(k)}` : ''}`)

export const listRuns = (limit = 50) => call<{ ok: boolean; runs: RunSummary[] }>(`${BASE}/runs?limit=${limit}`)

export const getRun = (id: string, allBlocks = false) =>
	call<{ ok: boolean; run: RunState }>(`${BASE}/runs/${id}${allBlocks ? '?all_blocks=true' : ''}`)

export const answerRun = (id: string, text: string) =>
	call<{ ok: boolean; run: RunState }>(`${BASE}/runs/${id}/answer`, { method: 'POST', body: { text } })

/** blockId (09.09) — точечная правка одного пункта: движок меняет только его, остальной текст и нумерация не трогаются. */
export const postMessage = (id: string, text: string, blockId?: string | null) =>
	call<{ ok: boolean; message_id: string; run: RunState }>(`${BASE}/runs/${id}/messages`, { method: 'POST', body: blockId ? { text, block_id: blockId } : { text } })

/** Пункт, выбранный для точечной правки: id блока, номер и текст для чипа над композером. */
export interface ClauseTarget {
	id: string
	n: string
	text: string
}

/** Правка руками (10.09): точечные правки по id блока (и строке) и удаления; base_version — версия, которую правили:
 *  если документ успел измениться, бэк ответит 409 и правка не ляжет поверх чужой. Без модели и списаний. */
export interface ManualEditBody {
	edits: { id: string; item?: number | null; v: string }[]
	removed: { id: string; item?: number | null }[]
	base_version?: number
}
export const manualEdit = (id: string, body: ManualEditBody) => call<{ ok: boolean; run: RunState }>(`${BASE}/runs/${id}/versions`, { method: 'POST', body })

/** Переименование работы (10.09); только владелец. Флаг «в боковом меню» ушёл 23.10: помощники попадают в меню сами. */
export const updateRun = (id: string, body: { title?: string }) =>
	call<{ ok: boolean; run: RunSummary }>(`${BASE}/runs/${id}`, { method: 'PATCH', body })

/** Помощник в боковом меню сайта (23.10): строка среди чатов — имя, аватарка, последняя своя работа (её открывает клик)
 *  и скрепка (та же, что в рельсе раздела). Приходит только у помощников, у которых есть хоть одна своя работа. */
export interface SidebarAssistant {
	agent_id: string
	agent_slug: string
	agent_title: string
	agent_icon_url: string | null
	run_id: string
	run_mode: 'solution' | 'chat' | string
	run_title: string
	created_at: string | null
	updated_at: string | null
	pinned: boolean
}
export const getSidebarAssistants = () => call<{ ok: boolean; assistants: SidebarAssistant[] }>(`${BASE}/runs/sidebar`)
/** Активность (22.09): идущие запуски + законченные за час, со слугом помощника —
 *  стикер на всём сайте и спиннеры рельс (см. api/assistantActivity.ts). */
export interface ActiveRun extends RunSummary { agent_slug?: string | null }
export const getActiveRuns = () => call<{ ok: boolean; runs: ActiveRun[] }>(`${BASE}/runs/active`)

export const retryRun = (id: string) => call<{ ok: boolean; run: RunState }>(`${BASE}/runs/${id}/retry`, { method: 'POST', body: {} })
export const cancelRun = (id: string) => call<{ ok: boolean; run: RunState }>(`${BASE}/runs/${id}/cancel`, { method: 'POST', body: {} })
export const deleteRun = (id: string) => call<{ ok: boolean }>(`${BASE}/runs/${id}`, { method: 'DELETE' })

export const versionFileUrl = (id: string, n: number, fmt: FileFormat) =>
	call<{ ok: boolean; url: string; fmt: string }>(`${BASE}/runs/${id}/versions/${n}/file?fmt=${fmt}`)

export const startChat = (agentId: string, text: string) =>
	call<{ ok: boolean; run: RunState }>(`${BASE}/chat`, { method: 'POST', body: { agent_id: agentId, text } })

// ── реквизиты (фаза 4) ──
export const listPartyProfiles = () => call<{ ok: boolean; items: PartyProfile[] }>(`${BASE}/party-profiles`)
export const createPartyProfile = (body: PartyProfileInput) =>
	call<{ ok: boolean; profile: PartyProfile }>(`${BASE}/party-profiles`, { method: 'POST', body })
export const updatePartyProfile = (id: string, body: PartyProfileInput) =>
	call<{ ok: boolean; profile: PartyProfile }>(`${BASE}/party-profiles/${id}`, { method: 'PATCH', body })
export const deletePartyProfile = (id: string) =>
	call<{ ok: boolean; default_id: string | null }>(`${BASE}/party-profiles/${id}`, { method: 'DELETE' })
/** Реквизиты по ИНН через реестр. Ошибки: bad_inn 400, not_found 404, unavailable/not_configured 503 — форма остаётся редактируемой. */
export const lookupInn = (inn: string) => call<{ ok: boolean } & LookupResult>(`${BASE}/party/lookup`, { method: 'POST', body: { inn } })
/** Банк по БИК: название в платёжном виде и корсчёт. Ошибки: bad_bik 400, not_found 404, unavailable 503. */
export const lookupBik = (bik: string) =>
	call<{ ok: boolean; found: boolean; bank: string; bik: string; corr: string; status?: string }>(`${BASE}/party/bank`, { method: 'POST', body: { bik } })

// ── данные рождения (астрология) ──
export const listBirthProfiles = () => call<{ ok: boolean; items: BirthProfile[] }>(`${BASE}/birth-profiles`)
export const createBirthProfile = (body: BirthValue & { is_default?: boolean }) =>
	call<{ ok: boolean; profile: BirthProfile }>(`${BASE}/birth-profiles`, { method: 'POST', body })
export const updateBirthProfile = (id: string, body: BirthValue & { is_default?: boolean }) =>
	call<{ ok: boolean; profile: BirthProfile }>(`${BASE}/birth-profiles/${id}`, { method: 'PATCH', body })
export const deleteBirthProfile = (id: string) =>
	call<{ ok: boolean; default_id: string | null }>(`${BASE}/birth-profiles/${id}`, { method: 'DELETE' })
/** Города для места рождения. Пустой список — подсказки недоступны, город берётся из списка во фронте. */
/** Состояние гороскопа: off — раздел выключен, building — период ещё собирается, locked — нужно открыть
 *  за кредиты, ready — текст пришёл. Платит читатель один раз за период (владелец 11.09). */
export type HoroscopeState = 'off' | 'building' | 'locked' | 'ready'
export interface HoroscopeReply { ok: boolean; state: HoroscopeState; horoscope: HoroscopePayload | null; price: number }
export const getHoroscope = (period: HoroscopePeriod) =>
	call<HoroscopeReply>(`${BASE}/horoscope?period=${period}`)
/** Витрина для поиска: один знак на сегодня, без входа. Тексты остальных знаков наружу не уходят. */
export interface PublicHoroscope {
	period: HoroscopePeriod; period_key: string; label: string
	sign: { key: string; title: string; glyph: string; text: string }
	signs: { key: string; title: string; glyph: string }[]
	sky: { retro: string[]; events: string[]; moon: string }
	disclaimer: string
}
export const getPublicHoroscope = (sign: string) =>
	call<{ ok: boolean; state: 'off' | 'building' | 'ready'; horoscope: PublicHoroscope | null; section_open: boolean }>(
		`${BASE}/horoscope/public?sign=${encodeURIComponent(sign)}`, { skipAuth: true })
/** Открыть период: списывает один раз, повторный вызов бесплатен. */
export const unlockHoroscope = (period: HoroscopePeriod) =>
	call<HoroscopeReply>(`${BASE}/horoscope/unlock?period=${period}`, { method: 'POST' })

export const suggestPlaces = (q: string) =>
	call<{ ok: boolean; items: PlaceSuggestion[] }>(`${BASE}/geo/suggest?q=${encodeURIComponent(q)}`)
/** Недавние адресаты из полей «Кому» прошлых запусков — чипы в модале «Реквизиты адресата». */
export const getRecentAddressees = () => call<{ ok: boolean; items: PartyValue[] }>(`${BASE}/party/recent`)
/** Следующий номер формы клиента («АКТ-2026-001») — подставляется в поле «Номер», клиент может поправить. */
export const getNextFormNumber = (form: FormKind) => call<{ ok: boolean; number: string }>(`${BASE}/forms/next-number?form=${form}`)
/** «Из истории» (09.09): документы клиента для поля с пометкой history и входы формы по выбранному документу. */
export const listFormSources = (solutionId: string, field: string) =>
	call<{ ok: boolean; items: SourceDoc[] }>(`${BASE}/forms/sources?solution_id=${encodeURIComponent(solutionId)}&field=${encodeURIComponent(field)}`)
export const prefillFromSource = (solutionId: string, field: string, sourceRunId: string) =>
	call<{ ok: boolean; mode: HistoryMode; inputs: Record<string, unknown>; source: SourceDoc }>(`${BASE}/forms/prefill`, { method: 'POST', body: { solution_id: solutionId, field, source_run_id: sourceRunId } })
/** Конструктор договора (09.09): поля из описания сделки и живой предпросмотр текста без списания. */
/** field — поле-описание конструктора; null (10.09) — текст из чата: заполняются все поля услуги. */
export const describeFill = (solutionId: string, field: string | null, text: string) =>
	call<{ ok: boolean } & DescribeResult>(`${BASE}/forms/describe`, { method: 'POST', body: field ? { solution_id: solutionId, field, text } : { solution_id: solutionId, text } })
/** «Заполнить из документа» (10.09): загруженный файл → поля формы решения; платно по цене модели («от»). */
export const extractFill = (solutionId: string, uploadId: string) =>
	call<{ ok: boolean } & ExtractResult>(`${BASE}/forms/extract`, { method: 'POST', body: { solution_id: solutionId, upload_id: uploadId } })
export const contractPreview = (solutionId: string, inputs: Record<string, unknown>) =>
	call<{ ok: boolean } & ContractPreview>(`${BASE}/contracts/preview`, { method: 'POST', body: { solution_id: solutionId, inputs } })

// ── совместный доступ (фаза 5) ──
export const getShare = (runId: string) => call<{ ok: boolean; share: ShareState }>(`${BASE}/runs/${runId}/share`)
export const updateShare = (runId: string, patch: { mode?: ShareMode; billing_default?: PayRule; allow_download?: boolean; approval_required?: boolean; approval_days?: number }) =>
	call<{ ok: boolean; share: ShareState }>(`${BASE}/runs/${runId}/share`, { method: 'PUT', body: patch })
/** Согласие своей стороны с последней версией (владелец или участник). */
export const approveRun = (runId: string) => call<{ ok: boolean; approval: ApprovalState; run: RunState }>(`${BASE}/runs/${runId}/approve`, { method: 'POST', body: {} })
export const inviteMember = (runId: string, body: { email: string; role: ShareRole; pay?: PayRule | null }) =>
	call<{ ok: boolean; share: ShareState }>(`${BASE}/runs/${runId}/share/members`, { method: 'POST', body })
export const updateMember = (runId: string, memberId: string, body: { role?: ShareRole; pay?: PayRule | null }) =>
	call<{ ok: boolean; share: ShareState }>(`${BASE}/runs/${runId}/share/members/${memberId}`, { method: 'PATCH', body })
export const removeMember = (runId: string, memberId: string) =>
	call<{ ok: boolean; share: ShareState }>(`${BASE}/runs/${runId}/share/members/${memberId}`, { method: 'DELETE' })
/** Документ по ссылке: без входа — просмотр/скачивание по правилу и комментарии с именем. Ошибки: not_found 404, share_off/gone 410. */
export const getSharedDoc = (token: string, allBlocks = false) =>
	call<{ ok: boolean; doc: SharedDoc }>(`${BASE}/d/${encodeURIComponent(token)}${allBlocks ? '?all_blocks=true' : ''}`)
export const sharedFileUrl = (token: string, n: number, fmt: FileFormat) =>
	call<{ ok: boolean; url: string; fmt: string }>(`${BASE}/d/${encodeURIComponent(token)}/versions/${n}/file?fmt=${fmt}`)
export const postSharedComment = (token: string, body: { text: string; name?: string }) =>
	call<{ ok: boolean; message_id: string; doc: SharedDoc }>(`${BASE}/d/${encodeURIComponent(token)}/comments`, { method: 'POST', body })
export const postSharedApprove = (token: string, name?: string) =>
	call<{ ok: boolean; approval: ApprovalState; doc: SharedDoc }>(`${BASE}/d/${encodeURIComponent(token)}/approve`, { method: 'POST', body: name ? { name } : {} })
export const postSharedEdit = (token: string, text: string) =>
	call<{ ok: boolean; message_id: string; doc: SharedDoc }>(`${BASE}/d/${encodeURIComponent(token)}/edits`, { method: 'POST', body: { text } })

// ── подпись (фаза 6) ──
export interface SignBody {
	mode: 'draw' | 'text' | 'saved'
	image?: string
	text?: string
	who?: string
	for_whom?: string
	save_to_profile?: boolean
	profile_id?: string
}
/** ПЭП на последнюю версию — только владелец; ошибки: already_signed/not_last 409, sign_disabled 403. */
export const signVersion = (runId: string, n: number, body: SignBody) =>
	call<{ ok: boolean; run: RunState }>(`${BASE}/runs/${runId}/versions/${n}/sign`, { method: 'POST', body })
export const getVerify = (key: string) => call<{ ok: boolean; found: boolean; signed: VerifyInfo | null }>(`${BASE}/verify/${encodeURIComponent(key.trim())}`)
export const setProfileSignature = (id: string, image: string) =>
	call<{ ok: boolean; profile: PartyProfile }>(`${BASE}/party-profiles/${id}/signature`, { method: 'POST', body: { image } })
export const deleteProfileSignature = (id: string) =>
	call<{ ok: boolean; profile: PartyProfile }>(`${BASE}/party-profiles/${id}/signature`, { method: 'DELETE' })

// ── отправка и сроки (фаза 7) ──
export const dispatchQuote = (runId: string, email?: string) =>
	call<{ ok: boolean; quote: DispatchQuote }>(`${BASE}/runs/${runId}/dispatch/quote`, { method: 'POST', body: { email: email ?? null } })
export const sendDispatch = (runId: string, body: SendBody) =>
	call<{ ok: boolean; result: SendResult; run: RunState }>(`${BASE}/runs/${runId}/dispatch`, { method: 'POST', body })
export const getDispatches = (runId: string) => call<{ ok: boolean; dispatch: DispatchState }>(`${BASE}/runs/${runId}/dispatches`)
export const cancelDispatch = (runId: string, dispatchId: string) =>
	call<{ ok: boolean; dispatch: Dispatch; state: DispatchState }>(`${BASE}/runs/${runId}/dispatches/${dispatchId}/cancel`, { method: 'POST', body: {} })
export const updateDispatch = (runId: string, dispatchId: string, body: { tracking?: string; status?: 'sent' | 'handed' }) =>
	call<{ ok: boolean; dispatch: Dispatch; state: DispatchState }>(`${BASE}/runs/${runId}/dispatches/${dispatchId}`, { method: 'PATCH', body })
export const setReminder = (runId: string, enabled: boolean) =>
	call<{ ok: boolean; reminder: Reminder | null }>(`${BASE}/runs/${runId}/reminder`, { method: 'PATCH', body: { enabled } })
/** Следующий шаг: префилл следующего решения этим документом (запуск — обычный createRun с parent_run_id). */
export const nextStep = (runId: string) => call<{ ok: boolean; prefill: NextPrefill }>(`${BASE}/runs/${runId}/next`, { method: 'POST', body: {} })

// ── «Награда за отзыв» (24.09): шаги в помощнике + опросник, токены после проверки ──────────

export type RewardStepKind = 'solution' | 'chat' | 'message'
export interface RewardStep {
	key: string
	kind: RewardStepKind
	/** Название решения: у закреплённого шага сразу, у умного дефолта — после зачёта. */
	title?: string | null
	solution_id?: string | null
	solution_slug?: string
	/** Подсказка незавершённому шагу умного дефолта: ещё не пройденное платное решение. */
	suggest_id?: string | null
	suggest_title?: string | null
	suggest_slug?: string | null
	done: boolean
	at?: string | null
	run_id?: string
}
// nps — шкала 1–10 («Порекомендуете помощника?», 02.10 вместо да/нет).
export type RewardQuestionType = 'scale' | 'choice' | 'text' | 'yes_no' | 'nps'
/** multi — можно выбрать несколько вариантов; when — вопрос показан, только когда оценка (вопрос key) в границах min–max (02.10). */
export interface RewardQuestion { key: string; label: string; type: RewardQuestionType; options?: string[]; multi?: boolean; when?: { key: string; min?: number; max?: number } }
export interface RewardProgram {
	shown: boolean
	reason: string | null
	reason_text: string | null
	mode: 'solutions' | 'chat'
	amount: number
	title: string
	survey: RewardQuestion[]
	min_review_chars: number
	/** Потолок отзыва (сервер, 02.10 — 5000); у старого сервера поля нет. */
	max_review_chars?: number
	pinned: boolean
}
export type RewardClaimStatus = 'submitted' | 'approved' | 'rejected'
export interface RewardClaim {
	status: RewardClaimStatus
	attempt: number
	amount: number
	submitted_at: string | null
	decided_at: string | null
	/** Причина отклонения — только у rejected. */
	note: string | null
	credited_amount: number | null
	can_resubmit: boolean
	answers?: Record<string, unknown>
	review?: string
}
export interface RewardState {
	program: RewardProgram
	steps: RewardStep[]
	done: number
	total: number
	complete: boolean
	claim: RewardClaim | null
	/** Сколько наград ещё доступно на аккаунте; null у гостя. */
	rewards_left: number | null
}
export const getRewardState = (agentId: string) =>
	call<{ ok: boolean } & RewardState>(`${BASE}/reward?agent_id=${encodeURIComponent(agentId)}`)
export const submitRewardClaim = (agentId: string, answers: Record<string, unknown>, review: string) =>
	call<{ ok: boolean; claim: RewardClaim }>(`${BASE}/reward/claims`, { method: 'POST', body: { agent_id: agentId, answers, review } })

